import { useQuery } from '@tanstack/react-query';
import type { inferInput, inferOutput } from '@trpc/tanstack-react-query';
import { useTRPC } from '../../../lib/trpc';
import type { CreativePerformer, LeaderboardRow } from './marketLeaderboardData';
import type { MarketRow } from './marketPerformanceData';
import type { MarketCoverage } from './types';
import { marketName } from './utils';

type TRPC = ReturnType<typeof useTRPC>;
export type MarketScope = inferInput<TRPC['mediaPerformance']['countries']>;
export type BreakdownScope = inferInput<TRPC['mediaPerformance']['fatigueTab']>;
type CountriesData = inferOutput<TRPC['mediaPerformance']['countries']>;
type LeaderboardData = inferOutput<TRPC['mediaPerformance']['countryLeaderboard']>;
type ApiCountryRow = CountriesData['countries'][number];
type ApiLeaderboardRow = LeaderboardData['weeks'][number]['countries'][number];
type ApiPerformer = ApiLeaderboardRow['highestPerformer'];

export type PerformanceTabData = inferOutput<TRPC['mediaPerformance']['performanceTab']>;
export type FatigueData = inferOutput<TRPC['mediaPerformance']['fatigueTab']>;
export type TrendData = inferOutput<TRPC['mediaPerformance']['trendTab']>;

function toMarketRow(country: ApiCountryRow, index: number): MarketRow {
  return {
    code: country.country,
    market: marketName(country.country),
    flag: country.country,
    spendMillions: country.spend / 1_000_000,
    savingsRate: country.savingsRate * 100,
    potentialSavingsK: country.potentialSavings / 1000,
    performanceIndex: country.performanceIndex,
    potentialUpliftK: country.potentialUplift === null ? null : country.potentialUplift / 1000,
    weekOverWeek: country.weekOverWeek === null ? null : -(country.weekOverWeek * 100),
    rank: index + 1,
  };
}

export function useMarketPerformance(
  scope: MarketScope,
  enabled: boolean,
): {
  data: { markets: MarketRow[] };
  currencySymbol: string;
  coverage: MarketCoverage | null;
  isLoading: boolean;
  isError: boolean;
} {
  const trpc = useTRPC();
  const query = useQuery({ ...trpc.mediaPerformance.countries.queryOptions(scope), enabled });
  const markets = (query.data?.countries ?? []).map(toMarketRow);
  return {
    data: { markets },
    currencySymbol: query.data?.currencySymbol ?? '',
    coverage: query.data
      ? {
          shown: query.data.countries.length,
          total: query.data.totalMarkets,
          spendShare: query.data.shownSpendShare * 100,
        }
      : null,
    isError: query.isError,
    isLoading: query.isPending && enabled,
  };
}

function toPerformer(performer: ApiPerformer): CreativePerformer {
  if (!performer) return { name: '-', creativeId: '-', upliftPercent: 0, thumbnailUrl: null };
  return {
    name: performer.creativeName,
    creativeId: performer.creativeId,
    upliftPercent: performer.uplift === null ? 0 : Math.round(performer.uplift * 100),
    thumbnailUrl: performer.thumbnailUrl,
    previewUrl: performer.previewUrl,
  };
}

function toLeaderboardRow(country: ApiLeaderboardRow, index: number): LeaderboardRow {
  return {
    code: country.country,
    market: marketName(country.country),
    flag: country.country,
    colorIndex: index,
    rank: country.rank,
    performanceIndex: country.performanceIndex,
    savingsRate: country.savingsRate * 100,
    potentialSavingsK: country.potentialSavings / 1000,
    spendMillions: country.spend / 1_000_000,
    // Backend WoW is the change in wasted spend (negative = waste fell); the table reads
    // positive as improvement, so flip the sign into an efficiency delta.
    weekOverWeek: country.weekOverWeek === null ? null : -(country.weekOverWeek * 100),
    highestPerformer: toPerformer(country.highestPerformer),
    lowestPerformer: toPerformer(country.lowestPerformer),
  };
}

export function usePerformanceTab(
  scope: BreakdownScope,
  side: 'over' | 'under',
  enabled: boolean,
): { data: PerformanceTabData | undefined; isLoading: boolean; isError: boolean } {
  const trpc = useTRPC();
  const query = useQuery({
    ...trpc.mediaPerformance.performanceTab.queryOptions({ ...scope, side }),
    enabled,
  });
  return { data: query.data, isError: query.isError, isLoading: query.isPending && enabled };
}

export function useFatigueTab(
  scope: BreakdownScope,
  enabled: boolean,
): { data: FatigueData | undefined; isLoading: boolean; isError: boolean } {
  const trpc = useTRPC();
  const query = useQuery({ ...trpc.mediaPerformance.fatigueTab.queryOptions(scope), enabled });
  return { data: query.data, isError: query.isError, isLoading: query.isPending && enabled };
}

export function useTrend(
  scope: BreakdownScope,
  enabled: boolean,
): { data: TrendData | undefined; isLoading: boolean; isError: boolean } {
  const trpc = useTRPC();
  const query = useQuery({ ...trpc.mediaPerformance.trendTab.queryOptions(scope), enabled });
  return { data: query.data, isError: query.isError, isLoading: query.isPending && enabled };
}

export function useMarketLeaderboard(
  scope: MarketScope,
  enabled: boolean,
): {
  weeks: { weekStart: string; rows: LeaderboardRow[]; coverage: MarketCoverage }[];
  availableWeeks: string[];
  currencySymbol: string;
  isLoading: boolean;
  isError: boolean;
} {
  const trpc = useTRPC();
  const query = useQuery({
    ...trpc.mediaPerformance.countryLeaderboard.queryOptions(scope),
    enabled,
  });
  return {
    weeks: (query.data?.weeks ?? []).map((week) => ({
      weekStart: week.weekStart,
      rows: week.countries.map(toLeaderboardRow),
      coverage: {
        shown: week.countries.length,
        total: week.totalMarkets,
        spendShare: week.shownSpendShare * 100,
      },
    })),
    availableWeeks: query.data?.availableWeeks ?? [],
    currencySymbol: query.data?.currencySymbol ?? '',
    isError: query.isError,
    isLoading: query.isPending && enabled,
  };
}
